// ---------------------------------------------------------------------------
// ContentWallPanel -- Posts the audience commented on (Enhanced)
// ---------------------------------------------------------------------------
// Features: media type filter pills, image-dominant cards, audience count
// shown once as subtitle, engagement glow on high-performing posts
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Heart, MessageSquare, Share2, Eye, Users, ChevronDown, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getPlatformIcon } from '@/components/v11/shared/platformIcons'
import type { ContentUniversePost, ContentSortField, SortOrder } from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContentWallPanelProps {
  readonly posts: readonly ContentUniversePost[]
  readonly hasMore: boolean
  readonly loading: boolean
  readonly sort: ContentSortField
  readonly order: SortOrder
  readonly onSortChange: (sort: ContentSortField, order: SortOrder) => void
  readonly onLoadMore: () => void
  readonly className?: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SORT_OPTIONS: readonly { readonly value: ContentSortField; readonly label: string }[] = [
  { value: 'audience_count', label: 'Audience Match' },
  { value: 'likes', label: 'Most Liked' },
  { value: 'comments', label: 'Most Comments' },
  { value: 'engagement_rate', label: 'Engagement Rate' },
  { value: 'post_date', label: 'Most Recent' },
]

type MediaFilter = 'all' | 'photo' | 'video' | 'carousel'

const MEDIA_FILTERS: readonly { readonly id: MediaFilter; readonly label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'photo', label: 'Photo' },
  { id: 'video', label: 'Video / Reel' },
  { id: 'carousel', label: 'Carousel' },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ContentWallPanel({
  posts,
  hasMore,
  loading,
  sort,
  order,
  onSortChange,
  onLoadMore,
  className,
}: ContentWallPanelProps) {
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all')

  const filteredPosts = useMemo(() => {
    if (mediaFilter === 'all') return posts
    return posts.filter(post => {
      const mt = (post.media_type ?? '').toLowerCase()
      switch (mediaFilter) {
        case 'photo':
          return mt === 'photo' || mt === 'image'
        case 'video':
          return mt === 'video' || mt === 'reel' || mt === 'reels' || post.is_video
        case 'carousel':
          return mt === 'carousel' || mt === 'carousel_album' || mt === 'sidecar'
        default:
          return true
      }
    })
  }, [posts, mediaFilter])

  return (
    <div className={cn('space-y-4', className)}>
      {/* Controls Bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Media type filter pills */}
        <div className="flex items-center gap-1">
          {MEDIA_FILTERS.map(filter => (
            <button
              key={filter.id}
              onClick={() => setMediaFilter(filter.id)}
              className={cn(
                'text-xs px-3 py-1.5 rounded-full font-medium transition-all',
                mediaFilter === filter.id
                  ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30'
                  : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-bg'
              )}
            >
              {filter.label}
            </button>
          ))}
          <span className="text-xs text-v11-text-muted ml-2">
            {filteredPosts.length} posts from your audience
          </span>
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as ContentSortField, order)}
            className="text-xs bg-v11-surface border border-v11-border rounded-lg px-3 py-1.5 text-v11-text-primary"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => onSortChange(sort, order === 'desc' ? 'asc' : 'desc')}
            className="text-xs px-2 py-1.5 rounded-lg border border-v11-border text-v11-text-muted hover:text-v11-text-primary transition-colors"
          >
            {order === 'desc' ? '\u2193' : '\u2191'}
          </button>
        </div>
      </div>

      {/* Post Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filteredPosts.map(post => (
          <ContentPostCard key={post.id} post={post} />
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={loading}
            className="border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover"
          >
            {loading ? 'Loading...' : 'Load More'}
            <ChevronDown className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ContentPostCard — image-dominant, compact, engagement glow
// ---------------------------------------------------------------------------

function ContentPostCard({ post }: { readonly post: ContentUniversePost }) {
  const truncatedCaption = post.caption
    ? post.caption.length > 60 ? `${post.caption.slice(0, 60)}...` : post.caption
    : ''

  // Engagement tier for visual glow
  const engagementRate = post.engagement_rate ?? 0
  const glowStyle = engagementRate > 10
    ? { boxShadow: '0 0 12px rgba(59, 130, 246, 0.15)' }
    : engagementRate > 5
      ? { boxShadow: '0 0 8px rgba(59, 130, 246, 0.08)' }
      : undefined

  return (
    <div
      className="rounded-xl border border-v11-border bg-v11-surface overflow-hidden hover:border-v11-text-muted/30 transition-all group"
      style={glowStyle}
    >
      {/* Media — takes priority, aspect square */}
      {post.media_url ? (
        <div className="relative aspect-square bg-v11-bg overflow-hidden">
          <img
            src={post.media_url}
            alt={post.caption ?? post.author}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
          {post.is_video && (
            <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
              <Play className="h-3 w-3 text-white fill-white" />
            </div>
          )}
          {/* Overlay: platform + author */}
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-2">
            <div className="flex items-center gap-1.5">
              {(() => {
                const platformIcon = getPlatformIcon(post.platform)
                if (!platformIcon) return null
                return (
                  <span className="flex-shrink-0 text-white/90" title={platformIcon.name}>
                    {platformIcon.icon}
                  </span>
                )
              })()}
              <span className="text-xs font-semibold text-white/90 truncate">
                {post.author}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* No media fallback — show author header */
        <div className="px-3 py-2 flex items-center gap-1.5 border-b border-v11-border">
          {(() => {
            const platformIcon = getPlatformIcon(post.platform)
            if (!platformIcon) return null
            return (
              <span className="flex-shrink-0" style={{ color: platformIcon.color }} title={platformIcon.name}>
                {platformIcon.icon}
              </span>
            )
          })()}
          <span className="text-xs font-semibold text-v11-text-primary truncate">{post.author}</span>
        </div>
      )}

      {/* Caption (compact) */}
      {truncatedCaption && (
        <div className="px-2.5 py-1.5">
          <p className="text-[11px] text-v11-text-secondary line-clamp-2">{truncatedCaption}</p>
        </div>
      )}

      {/* Audience + Emotion row */}
      <div className="px-2.5 py-1.5 flex items-center justify-between gap-1 border-t border-v11-border/50">
        <div className="flex items-center gap-1">
          <Users className="h-3 w-3 text-blue-400" />
          <span className="text-xs font-bold text-blue-400 tabular-nums">
            {post.audience_count.toLocaleString()}
          </span>
        </div>
        {post.media_type && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-v11-bg text-v11-text-muted">
            {post.media_type}
          </span>
        )}
        {post.emotion && (
          <span className={cn(
            'text-[9px] font-semibold px-1.5 py-0.5 rounded-full capitalize',
            getEmotionChipStyle(post.emotion)
          )}>
            {post.emotion}
          </span>
        )}
      </div>

      {/* Metrics row — compact icons */}
      <div className="grid grid-cols-4 gap-0 border-t border-v11-border/50">
        <MetricCell icon={<Heart className="h-3 w-3" />} value={formatShort(post.likes)} />
        <MetricCell icon={<MessageSquare className="h-3 w-3" />} value={formatShort(post.comments_count)} />
        <MetricCell icon={<Share2 className="h-3 w-3" />} value={formatShort(post.share_count)} />
        <MetricCell icon={<Eye className="h-3 w-3" />} value={formatShort(post.views_count)} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MetricCell
// ---------------------------------------------------------------------------

function MetricCell({ icon, value }: { readonly icon: React.ReactNode; readonly value: string }) {
  return (
    <div className="flex items-center justify-center gap-1 py-1.5 text-v11-text-muted">
      {icon}
      <span className="text-[10px] tabular-nums">{value}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

const POSITIVE_EMOTIONS = new Set(['joy', 'excitement', 'love', 'admiration', 'hope', 'pride', 'gratitude', 'solidarity', 'relief'])
const NEGATIVE_EMOTIONS = new Set(['frustration', 'annoyance', 'confusion', 'disappointment', 'sadness', 'hatred', 'fear', 'irony'])

function getEmotionChipStyle(emotion: string): string {
  const lower = emotion.toLowerCase()
  if (POSITIVE_EMOTIONS.has(lower)) return 'bg-green-500/15 text-green-400'
  if (NEGATIVE_EMOTIONS.has(lower)) return 'bg-red-500/15 text-red-400'
  return 'bg-amber-500/15 text-amber-400'
}

export { ContentWallPanel }
export type { ContentWallPanelProps }
